import React, { useContext, useState, useRef } from 'react';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { useFormik } from 'formik';
import { getRequest, postRequest } from '../../../api/ApiController';
import { SaveIcon, AddIcon, CopyIcon, VerticalLineIcon, FavoriteIcon, PriceIcon, SaveAndCloseIcon } from '../../svg/svgIcons';
import AuthContext from '../../../components/AuthContext';
import UtilContext from '../../../components/UtilContext';
import { LocaleContext } from '../../../components/LocaleContext';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';

const ADD_MASK = 'trng035';

function lecturerFormSeed(lecturer, type) {
  if (!lecturer || type === 'add') {
    return {
      name: lecturer?.name ?? '',
      description: lecturer?.description ?? '',
    };
  }
  return lecturer;
}

function lecturerRowFromResponse(response) {
  const row = response?.data;
  if (row && typeof row === 'object' && !Array.isArray(row) && row.id != null) return row;
  return null;
}

export default function LecturerAdd({ lecturer, type = 'add' }) {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t } = useContext(LocaleContext);
  const { openFavoriteDialog, toast, setFastView, setVisible, setDialogText } = useContext(UtilContext);

  const [closeTab, setCloseTab] = useState(false);
  const submitRef = useRef(null);

  const [formLecturer, setFormLecturer] = useState(() => lecturerFormSeed(lecturer, type));
  const [formType, setFormType] = useState(type);

  const canAdd = user.permissions?.includes('add training-setup');
  const canEdit = user.permissions?.includes('edit training-setup');
  const saveDisabled = formType === 'add' ? !canAdd : !canEdit;

  function openAddTab(nextLecturer) {
    dispatch({
      type: 'create_new_tab',
      name: t('LecturerList.Tab.New'), translationKey: 'LecturerList.Tab.New', legacyLabel: t('LecturerList.Tab.New'),
      page: 'LecturerAdd',
      props: { lecturer: nextLecturer, type: 'add' },
      mask: ADD_MASK,
    });
  }

  const formik = useFormik({
    initialValues: formLecturer == null ? { name: '', description: '' } : formLecturer,
    enableReinitialize: true,
    onSubmit: (values) => {
      setDialogText(t('Common.Saving'));
      setVisible(true);
      const formData = new FormData();
      formData.append('name', values.name ?? '');
      formData.append('description', values.description ?? '');
      const url = formType === 'add' ? '/api/lecturers' : `/api/lecturers/update/${formik.values.id}`;
      postRequest(url, formData, true)
        .then((saveResponse) => {
          const savedRow = lecturerRowFromResponse(saveResponse);
          return getRequest('/api/lecturers').then((listResponse) => {
            dispatch({ type: 'load_lecturers', newLecturers: listResponse.data });
            if (savedRow) {
              formik.setValues(savedRow);
              setFormLecturer(savedRow);
            }
            if (closeTab) dispatch({ type: 'close_tab', id: state.selectedTab });
            setFormType('edit');
            setVisible(false);
            toast?.current?.show({
              severity: 'info',
              summary: t('LecturerList.Toast.DeletedSummary'),
              life: 3000,
              detail: formType === 'add' ? t('LecturerAdd.Toast.Saved') : t('LecturerAdd.Toast.Updated'),
            });
          });
        })
        .catch(() => {
          setVisible(false);
          alert(t('LecturerAdd.Toast.Failed'));
        });
    },
  });

  return (
    <>
      <div className="w-full h-10 flex flex-row bg-[#333]">
        <IntersectionObserverWrapper>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="saveandclose"><button className="disabled:opacity-25" disabled={saveDisabled} type="submit" onClick={() => { setCloseTab(true); submitRef.current.click(); }}><SaveAndCloseIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="save"><button className="disabled:opacity-25" disabled={saveDisabled} type="submit" onClick={() => submitRef.current.click()}><SaveIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="add"><button className="disabled:opacity-25" disabled={!canAdd} onClick={() => openAddTab(null)}><AddIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="copy"><button className="disabled:opacity-25" disabled={!canAdd} onClick={() => openAddTab({ name: formik.values.name, description: formik.values.description })}><CopyIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite"><button onClick={() => openFavoriteDialog(t('LecturerList.Tab.New'), 'LecturerAdd')}><FavoriteIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="fast"><button onClick={() => setFastView('LecturerAdd', null, null)}><PriceIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
        </IntersectionObserverWrapper>
      </div>
      <form onSubmit={formik.handleSubmit}>
        <div className="relative flex flex-col overflow-x min-h-[calc(100vh-177px)]">
          <div className="xl:grid grid-cols-2 gap-1 mb-2 ">
            <div className="w-auto p-6 m-4 bg-gray-100 rounded-md shadow-md">
              <h1 className="text-3xl font-semibold text-left text-zinc-900">
                {t('LecturerAdd.Title')}
              </h1>
              {formType === 'edit' && <label className="block text-md font-semibold text-gray-800">{t('LecturerAdd.Label.Id')}: {formik.values.id}</label>}
              <div>
                <label htmlFor="name" className="block text-md font-semibold text-gray-800">
                  {t('LecturerAdd.Label.Name')}
                </label>
                <input
                  name="name"
                  id="name"
                  type="text"
                  required
                  onChange={formik.handleChange}
                  value={formik.values.name}
                  className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                />
              </div>
              <div>
                <label htmlFor="description" className="block text-md font-semibold text-gray-800">
                  {t('LecturerAdd.Label.Description')}
                </label>
                <textarea
                  name="description"
                  id="description"
                  onChange={formik.handleChange}
                  value={formik.values.description}
                  className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                />
              </div>
              <button type="submit" ref={submitRef} className="hidden" />
            </div>
          </div>
        </div>
      </form>
    </>
  );
}
